import React from "react";
import {
  AbsoluteFill,
  Easing,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
} from "remotion";

const C = {
  night: "#050816",
  white: "#F7F8FF",
  cyan: "#62E7FF",
  blue: "#5269FF",
  violet: "#9C6CFF",
  pink: "#FF65BF",
  coral: "#FF7057",
  lime: "#C8FF62",
};

const clamp = {
  extrapolateLeft: "clamp" as const,
  extrapolateRight: "clamp" as const,
};

const silky = Easing.bezier(0.16, 1, 0.3, 1);
const cameraEase = Easing.bezier(0.65, 0, 0.35, 1);

const move = (
  frame: number,
  from: number,
  to: number,
  easing: (value: number) => number = silky,
) =>
  interpolate(frame, [from, to], [0, 1], {
    ...clamp,
    easing,
  });

const visible = (
  frame: number,
  enter: [number, number],
  exit: [number, number],
) =>
  interpolate(frame, [...enter, ...exit], [0, 1, 1, 0], {
    ...clamp,
    easing: cameraEase,
  });

const PhotoPlate: React.FC<{
  src: string;
  frame: number;
  align?: "left" | "center" | "right";
  strength?: number;
}> = ({ src, frame, align = "center", strength = 0.72 }) => {
  const drift = interpolate(frame, [0, 126], [0, 1], clamp);
  const direction = align === "left" ? -1 : align === "right" ? 1 : 0.25;
  const scale = interpolate(drift, [0, 1], [1.14, 1.04]);
  const x = interpolate(drift, [0, 1], [direction * -45, direction * 45]);
  const blur = interpolate(frame, [0, 20], [9, 0], clamp);

  return (
    <div className="gh-photo-plate" style={{ opacity: strength }}>
      <Img
        src={staticFile(src)}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: align,
          filter: `blur(${blur}px) saturate(1.08) contrast(1.04)`,
          transform: `translate3d(${x}px, 0, 0) scale(${scale})`,
          translate: "-206.8px 33.6px",
        }}
        from={-4}
      />
      <div className="gh-photo-grade" />
    </div>
  );
};

const hudCamera = (frame: number) => {
  const times = [0, 84, 145, 214, 275, 344, 405, 468, 535, 630];

  return {
    x: interpolate(
      frame,
      times,
      [0, 0, -2120, -2120, -2120, -2120, 0, 0, 0, 0],
      { ...clamp, easing: cameraEase },
    ),
    y: interpolate(
      frame,
      times,
      [0, 0, 0, 0, -1260, -1260, -1260, -1260, -2520, -2520],
      { ...clamp, easing: cameraEase },
    ),
    scale: interpolate(
      frame,
      times,
      [1, 1.05, 0.94, 1.04, 0.92, 1.06, 0.94, 1.08, 0.94, 1.04],
      { ...clamp, easing: cameraEase },
    ),
    rotation: interpolate(
      frame,
      times,
      [0, -1, 1.7, 0, -1.8, 0.8, 1.4, 0, -1.2, 0],
      { ...clamp, easing: cameraEase },
    ),
  };
};

const ArrowIcon: React.FC<{ direction?: "right" | "up" }> = ({
  direction = "right",
}) => (
  <svg
    viewBox="0 0 48 48"
    aria-hidden="true"
    style={{ transform: direction === "up" ? "rotate(-45deg)" : undefined }}
  >
    <path
      d="M8 24h30M27 12l12 12-12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SparkIcon: React.FC = () => (
  <svg viewBox="0 0 48 48" aria-hidden="true">
    <path
      d="M24 5c1 11 7 17 19 19-12 2-18 8-19 19-2-11-8-17-19-19C16 22 22 16 24 5Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
    />
    <circle cx="37" cy="10" r="3" fill="currentColor" />
  </svg>
);

const Radar: React.FC<{ frame: number }> = ({ frame }) => {
  const rotation = interpolate(frame, [0, 120], [-60, 220], clamp);
  const pulse = 0.82 + Math.sin(frame / 7) * 0.08;

  return (
    <svg className="gh-radar" viewBox="0 0 360 360" aria-hidden="true">
      <circle
        cx="180"
        cy="180"
        r="156"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        opacity="0.25"
      />
      <circle
        cx="180"
        cy="180"
        r="112"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        opacity="0.45"
        strokeDasharray="6 12"
      />
      <circle
        cx="180"
        cy="180"
        r="68"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        opacity="0.7"
      />
      <g transform={`rotate(${rotation} 180 180)`}>
        <path
          d="M180 180 180 22 A158 158 0 0 1 305 84Z"
          fill="url(#radar-fill)"
          opacity="0.5"
        />
        <line
          x1="180"
          y1="180"
          x2="180"
          y2="22"
          stroke="currentColor"
          strokeWidth="3"
        />
      </g>
      <circle cx="180" cy="180" r={14 * pulse} fill={C.cyan} />
      <circle cx="112" cy="116" r="8" fill={C.pink} />
      <circle cx="264" cy="232" r="7" fill={C.lime} />
      <defs>
        <linearGradient id="radar-fill" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor={C.cyan} stopOpacity="0" />
          <stop offset="1" stopColor={C.cyan} stopOpacity="0.85" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const HudIntro: React.FC<{ frame: number }> = ({ frame }) => {
  const intro = spring({
    frame,
    fps: 30,
    config: { damping: 22, stiffness: 92, mass: 1 },
  });
  const opacity = visible(frame, [0, 18], [96, 126]);
  const scan = interpolate(frame, [0, 126], [-25, 115], clamp);
  const blur = interpolate(intro, [0, 1], [22, 0]);

  return (
    <section className="gh-station gh-intro" style={{ opacity }}>
      <PhotoPlate
        src="backgrounds/glass-atrium.png"
        frame={frame}
        align="left"
        strength={0.68}
      />
      <div className="gh-station-label">01 / SIGNAL ACQUIRED</div>
      <div
        className="gh-intro-copy"
        style={{
          opacity: intro,
          filter: `blur(${blur}px)`,
          transform: `translateY(${(1 - intro) * 90}px) scale(${0.94 + intro * 0.06})`,
        }}
      >
        <span>Advanced visual systems</span>
        <h1>
          Design at the
          <br />
          speed of <em>light.</em>
        </h1>
      </div>
      <div
        className="gh-scan-light"
        style={{ transform: `translateX(${scan}%) rotate(14deg)` }}
      />
      <div
        className="gh-radar-card gh-glass"
        style={{
          opacity: intro,
          transform: `translate3d(${(1 - intro) * 100}px, 0, 0) rotate(${(1 - intro) * 6}deg)`,
        }}
      >
        <div className="gh-card-head">
          <span>LIVE SCAN</span>
          <i />
        </div>
        <Radar frame={frame} />
        <div className="gh-card-foot">
          <span>SYNC 98.4%</span>
          <span>NODE / 047</span>
        </div>
      </div>
      <div className="gh-mini-hud gh-glass">
        <span>ENERGY</span>
        <strong>
          {Math.round(interpolate(frame, [0, 110], [12, 96], clamp))}%
        </strong>
        <div>
          <i style={{ transform: `scaleX(${move(frame, 18, 80)})` }} />
        </div>
      </div>
    </section>
  );
};

const GlassModule: React.FC<{
  title: string;
  caption: string;
  color: string;
  delay: number;
  frame: number;
  icon: "spark" | "arrow";
}> = ({ title, caption, color, delay, frame, icon }) => {
  const enter = spring({
    frame: frame - delay,
    fps: 30,
    config: { damping: 20, stiffness: 105, mass: 0.88 },
  });
  const shine = interpolate(
    frame,
    [delay + 15, delay + 85],
    [-100, 170],
    clamp,
  );

  return (
    <article
      className="gh-module gh-glass"
      style={{
        opacity: enter,
        transform: `translateY(${(1 - enter) * 130}px) rotateX(${(1 - enter) * 12}deg)`,
      }}
    >
      <div
        className="gh-module-icon"
        style={{ color, backgroundColor: `${color}24` }}
      >
        {icon === "spark" ? <SparkIcon /> : <ArrowIcon direction="up" />}
      </div>
      <span>{caption}</span>
      <h3>{title}</h3>
      <div className="gh-module-arrow">
        <ArrowIcon />
      </div>
      <div
        className="gh-glass-shine"
        style={{ transform: `translateX(${shine}%) rotate(18deg)` }}
      />
    </article>
  );
};

const GlassSystem: React.FC<{ frame: number }> = ({ frame }) => {
  const opacity = visible(frame, [0, 20], [98, 126]);
  const title = move(frame, 0, 34);

  return (
    <section className="gh-station gh-system" style={{ opacity }}>
      <PhotoPlate
        src="backgrounds/light-gallery.png"
        frame={frame}
        strength={0.62}
      />
      <div className="gh-station-label">02 / GLASS SYSTEM</div>
      <h2
        className="gh-system-title"
        style={{
          opacity: title,
          filter: `blur(${interpolate(title, [0, 1], [16, 0])}px)`,
          transform: `translateX(${(1 - title) * -100}px)`,
        }}
      >
        Clear layers.
        <br />
        <em>Deep meaning.</em>
      </h2>
      <div className="gh-module-grid">
        <GlassModule
          title="Adaptive"
          caption="INTERFACE 01"
          color={C.cyan}
          delay={8}
          frame={frame}
          icon="spark"
        />
        <GlassModule
          title="Responsive"
          caption="INTERFACE 02"
          color={C.pink}
          delay={16}
          frame={frame}
          icon="arrow"
        />
        <GlassModule
          title="Connected"
          caption="INTERFACE 03"
          color={C.lime}
          delay={24}
          frame={frame}
          icon="spark"
        />
      </div>
      <div className="gh-dock gh-glass">
        {["⌘", "✦", "↗", "◎", "•••"].map((item, index) => (
          <span
            key={item}
            style={{
              transform: `translateY(${(1 - spring({ frame: frame - index * 4, fps: 30, config: { damping: 18 } })) * 40}px)`,
            }}
          >
            {item}
          </span>
        ))}
      </div>
    </section>
  );
};

const WaveField: React.FC<{ frame: number }> = ({ frame }) => {
  const paths = Array.from({ length: 9 });

  return (
    <svg className="gh-wave-field" viewBox="0 0 1500 620" aria-hidden="true">
      <defs>
        <linearGradient id="wave-gradient" x1="0" x2="1">
          <stop offset="0" stopColor={C.cyan} />
          <stop offset="0.5" stopColor={C.violet} />
          <stop offset="1" stopColor={C.pink} />
        </linearGradient>
        <filter id="wave-glow">
          <feGaussianBlur stdDeviation="7" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      {paths.map((_, index) => {
        const y = 90 + index * 54;
        const phase = frame * 3.2 + index * 36;
        const amplitude = 36 + Math.sin((frame + index * 8) / 18) * 18;
        const d = `M -100 ${y} C 180 ${y - amplitude}, 330 ${y + amplitude}, 600 ${y} S 1020 ${y - amplitude}, 1600 ${y}`;

        return (
          <path
            key={index}
            d={d}
            fill="none"
            stroke="url(#wave-gradient)"
            strokeWidth={index === 4 ? 5 : 2}
            strokeLinecap="round"
            strokeDasharray="28 18"
            strokeDashoffset={-phase}
            opacity={0.22 + index * 0.055}
            filter={index === 4 ? "url(#wave-glow)" : undefined}
          />
        );
      })}
    </svg>
  );
};

const WaveStation: React.FC<{ frame: number }> = ({ frame }) => {
  const opacity = visible(frame, [0, 18], [102, 126]);
  const reveal = move(frame, 0, 38);
  const arrowX = interpolate(frame, [0, 126], [-260, 420], {
    ...clamp,
    easing: cameraEase,
  });

  return (
    <section className="gh-station gh-waves" style={{ opacity }}>
      <PhotoPlate
        src="backgrounds/glass-waves.png"
        frame={frame}
        align="left"
        strength={0.78}
      />
      <div className="gh-station-label">03 / FLOW FIELD</div>
      <WaveField frame={frame} />
      <div
        className="gh-wave-copy gh-glass"
        style={{
          opacity: reveal,
          backdropFilter: `blur(${interpolate(reveal, [0, 1], [0, 28])}px)`,
          transform: `translateY(${(1 - reveal) * 110}px)`,
        }}
      >
        <span>MOTION PRINCIPLE / 03</span>
        <h2>
          Follow the
          <br />
          <em>energy.</em>
        </h2>
      </div>
      <div
        className="gh-flight-arrow"
        style={{
          transform: `translate3d(${arrowX}px, ${Math.sin(frame / 13) * 45}px, 0) rotate(-8deg)`,
        }}
      >
        <ArrowIcon />
      </div>
      <div className="gh-wave-data">
        <span>AMPLITUDE</span>
        <strong>
          0.{String(Math.round(62 + Math.sin(frame / 9) * 14)).padStart(2, "0")}
        </strong>
        <span>VELOCITY</span>
        <strong>{Math.round(120 + Math.sin(frame / 15) * 28)} px/s</strong>
      </div>
    </section>
  );
};

const SwipeFinale: React.FC<{ frame: number }> = ({ frame }) => {
  const opacity = visible(frame, [0, 18], [106, 126]);
  const reveal = move(frame, 4, 42);
  const swipe = move(frame, 55, 106, Easing.bezier(0.8, 0, 0.2, 1));
  const blur = interpolate(reveal, [0, 1], [30, 0]);

  return (
    <section className="gh-station gh-finale" style={{ opacity }}>
      <PhotoPlate
        src="backgrounds/light-gallery.png"
        frame={frame + 22}
        align="right"
        strength={0.68}
      />
      <div className="gh-station-label">04 / PREMIUM MOTION</div>
      <div
        className="gh-final-light"
        style={{
          opacity: reveal,
          transform: `translateX(${interpolate(swipe, [0, 1], [-18, 32])}%) rotate(-16deg)`,
        }}
      />
      <div
        className="gh-final-copy"
        style={{
          opacity: reveal,
          filter: `blur(${blur}px)`,
          transform: `translate3d(0, ${(1 - reveal) * 100}px, 0) scale(${0.9 + reveal * 0.1})`,
        }}
      >
        <span>One system / infinite expression</span>
        <h2>
          Shape the
          <br />
          <em>future.</em>
        </h2>
      </div>
      <div className="gh-final-pills">
        {["GLASS", "LIGHT", "DEPTH", "FLOW"].map((item, index) => {
          const enter = spring({
            frame: frame - 18 - index * 6,
            fps: 30,
            config: { damping: 19, stiffness: 105 },
          });

          return (
            <span
              className="gh-glass"
              key={item}
              style={{
                opacity: enter,
                transform: `translateY(${(1 - enter) * 70}px)`,
              }}
            >
              {item}
            </span>
          );
        })}
      </div>
      <div className="gh-swipe" style={{ transform: `scaleX(${swipe})` }} />
    </section>
  );
};

const DesignCursor: React.FC<{ frame: number }> = ({ frame }) => {
  const times = [0, 18, 42, 67, 92, 116, 146];
  const x = interpolate(frame, times, [1480, 320, 860, 1285, 1020, 1450, 900], {
    ...clamp,
    easing: cameraEase,
  });
  const y = interpolate(frame, times, [820, 175, 430, 715, 300, 175, 610], {
    ...clamp,
    easing: cameraEase,
  });
  const clickFrames = [18, 42, 67, 92, 116, 146];
  const click = Math.max(
    ...clickFrames.map((at) =>
      interpolate(frame, [at, at + 4, at + 10], [0, 1, 0], clamp),
    ),
  );

  return (
    <>
      <div
        className="gh-design-ripple"
        style={{
          left: x,
          top: y,
          opacity: click,
          transform: `translate(-50%, -50%) scale(${0.25 + click * 1.2})`,
        }}
      />
      <div
        className="gh-design-cursor"
        style={{
          left: x,
          top: y,
          transform: `translate(-5px, -4px) scale(${1 - click * 0.16})`,
        }}
      >
        <svg viewBox="0 0 48 56" aria-hidden="true">
          <path
            d="M5 4 39 35 23 38 15 52Z"
            fill={C.white}
            stroke={C.night}
            strokeWidth="3"
            strokeLinejoin="round"
          />
        </svg>
        <span>DESIGNER</span>
      </div>
    </>
  );
};

const SelectionBox: React.FC<{
  frame: number;
  range: [number, number];
  position: { left: number; top: number; width: number; height: number };
  color: string;
  label: string;
}> = ({ frame, range, position, color, label }) => {
  const opacity = interpolate(
    frame,
    [range[0] - 5, range[0], range[1], range[1] + 5],
    [0, 1, 1, 0],
    clamp,
  );

  return (
    <div
      className="gh-selection"
      style={{
        ...position,
        color,
        opacity,
        borderColor: color,
      }}
    >
      <span>{label}</span>
      <i className="gh-handle gh-handle-tl" />
      <i className="gh-handle gh-handle-tr" />
      <i className="gh-handle gh-handle-bl" />
      <i className="gh-handle gh-handle-br" />
    </div>
  );
};

const WebsiteDesignLab: React.FC<{ frame: number }> = ({ frame }) => {
  const opacity = visible(frame, [0, 24], [132, 150]);
  const workspace = spring({
    frame,
    fps: 30,
    config: { damping: 22, stiffness: 88, mass: 1 },
  });
  const typed = Math.floor(
    interpolate(frame, [30, 68], [0, "DESIGN WITH CLARITY".length], clamp),
  );
  const title = "DESIGN WITH CLARITY".slice(0, typed);
  const imageReveal = move(frame, 64, 92);
  const cardReveal = move(frame, 88, 122);
  const buttonReveal = spring({
    frame: frame - 108,
    fps: 30,
    config: { damping: 18, stiffness: 115 },
  });
  const accentMix = move(frame, 112, 138);

  return (
    <section className="gh-station gh-design-lab" style={{ opacity }}>
      <PhotoPlate
        src="backgrounds/glass-atrium.png"
        frame={frame + 16}
        align="center"
        strength={0.38}
      />
      <div className="gh-station-label">05 / LIVE DESIGN LAB</div>

      <div
        className="gh-browser gh-glass"
        style={{
          opacity: workspace,
          transform: `translateY(${(1 - workspace) * 130}px) scale(${0.92 + workspace * 0.08})`,
        }}
      >
        <div className="gh-browser-bar">
          <div className="gh-browser-dots">
            <i />
            <i />
            <i />
          </div>
          <div className="gh-browser-url">canvas.design / untitled-page</div>
          <div className="gh-browser-actions">100% &nbsp; •••</div>
        </div>

        <aside className="gh-design-sidebar">
          <span className="gh-sidebar-label">INSERT</span>
          <button type="button">T</button>
          <button type="button">□</button>
          <button type="button">○</button>
          <button type="button">↗</button>
          <span className="gh-sidebar-label">COLOR</span>
          <i style={{ background: C.cyan }} />
          <i style={{ background: C.violet }} />
          <i style={{ background: C.pink }} />
          <i style={{ background: C.lime }} />
        </aside>

        <div className="gh-page-canvas">
          <div className="gh-page-nav">
            <span>STUDIO / 24</span>
            <div>WORK &nbsp;&nbsp; ABOUT &nbsp;&nbsp; CONTACT</div>
          </div>
          <div className="gh-page-grid">
            <div className="gh-page-copy">
              <span className="gh-page-kicker">CREATIVE DIGITAL STUDIO</span>
              <h2>
                {title}
                <i />
              </h2>
              <p>
                Interfaces shaped through motion, systems, and a clear point of
                view.
              </p>
              <div
                className="gh-page-button"
                style={{
                  opacity: buttonReveal,
                  background: `linear-gradient(90deg, ${C.cyan}, ${accentMix > 0.5 ? C.pink : C.violet})`,
                  transform: `translateY(${(1 - buttonReveal) * 35}px)`,
                }}
              >
                EXPLORE WORK <ArrowIcon />
              </div>
            </div>

            <div
              className="gh-page-image"
              style={{
                clipPath: `inset(${(1 - imageReveal) * 100}% 0 0 0 round 38px)`,
                transform: `scale(${1.08 - imageReveal * 0.08})`,
              }}
            >
              <Img src={staticFile("backgrounds/glass-waves.png")} />
              <div className="gh-image-badge gh-glass">FEATURED / 001</div>
            </div>
          </div>

          <div className="gh-page-cards">
            {["STRATEGY", "INTERFACE", "MOTION"].map((item, index) => (
              <div
                key={item}
                style={{
                  opacity: cardReveal,
                  transform: `translateY(${(1 - cardReveal) * (45 + index * 15)}px)`,
                  borderColor: [C.cyan, C.violet, C.pink][index],
                }}
              >
                <span>0{index + 1}</span>
                <strong>{item}</strong>
              </div>
            ))}
          </div>

          <SelectionBox
            frame={frame}
            range={[14, 42]}
            position={{ left: 72, top: 104, width: 490, height: 156 }}
            color={C.cyan}
            label="TEXT / H1"
          />
          <SelectionBox
            frame={frame}
            range={[62, 92]}
            position={{ left: 642, top: 78, width: 480, height: 390 }}
            color={C.pink}
            label="IMAGE / HERO"
          />
          <SelectionBox
            frame={frame}
            range={[104, 138]}
            position={{ left: 72, top: 320, width: 270, height: 68 }}
            color={C.lime}
            label="BUTTON / CTA"
          />
        </div>

        <div className="gh-properties gh-glass">
          <div>
            <span>X</span>
            <strong>072</strong>
            <span>Y</span>
            <strong>320</strong>
          </div>
          <div>
            <span>W</span>
            <strong>270</strong>
            <span>H</span>
            <strong>068</strong>
          </div>
          <div className="gh-property-color">
            <i />
            <span>ACCENT</span>
            <strong>#62E7FF</strong>
          </div>
        </div>

        <DesignCursor frame={frame} />
      </div>
    </section>
  );
};

const GlassWorld: React.FC<{ frame: number }> = ({ frame }) => {
  const camera = hudCamera(frame);

  return (
    <div
      className="gh-world"
      style={{
        transform: `translate3d(${camera.x}px, ${camera.y}px, 0) scale(${camera.scale}) rotate(${camera.rotation}deg)`,
      }}
    >
      <div className="gh-aurora gh-aurora-a" />
      <div className="gh-aurora gh-aurora-b" />
      <div className="gh-world-grid" />
      <HudIntro frame={frame} />
      <GlassSystem frame={frame - 120} />
      <WaveStation frame={frame - 240} />
      <SwipeFinale frame={frame - 360} />
      <WebsiteDesignLab frame={frame - 480} />
    </div>
  );
};

const ScreenHud: React.FC<{ frame: number }> = ({ frame }) => {
  const stage = Math.min(5, Math.floor(frame / 120) + 1);
  const progress = interpolate(frame, [0, 630], [0, 1], clamp);

  return (
    <div className="gh-screen-hud">
      <div className="gh-screen-top">
        <span>NEURAL MOTION / GLASS OS</span>
        <span>SCENE 0{stage} / 05</span>
      </div>
      <div className="gh-progress">
        <i style={{ transform: `scaleX(${progress})` }} />
      </div>
      <div className="gh-screen-bottom">
        <span>LIVE</span>
        <span>{String(frame).padStart(4, "0")} F</span>
      </div>
    </div>
  );
};

export const GlassHudJourney: React.FC = () => {
  const frame = useCurrentFrame();
  const intro = move(frame, 0, 18);
  const end = interpolate(frame, [612, 630], [1, 0], {
    ...clamp,
    easing: Easing.in(Easing.cubic),
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: C.night,
        opacity: intro * end,
        overflow: "hidden",
      }}
    >
      <GlassWorld frame={frame} />
      <ScreenHud frame={frame} />
      <div className="gh-screen-vignette" />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
